import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, DollarSign, Scale, FolderKanban, Heart, Briefcase, Plus, User, Dumbbell, Brain, Compass, Palette } from 'lucide-react';
type AgentStatus = 'active' | 'inactive';
interface SessionEntry {
  title: string;
  time: string;
  summary: string;
}
interface Action {
  text: string;
  due?: string;
  done: boolean;
}
interface Appointment {
  title: string;
  when: string;
  recurring?: boolean;
}
interface Agent {
  id: string;
  name: string;
  icon: React.FC<{
    size?: number;
    className?: string;
  }>;
  colour: string;
  status: AgentStatus;
  purpose?: string;
  voice?: string;
  lastSpoke?: string;
  skills?: string[];
  soul?: {
    goals: string[];
    context: string[];
    preferences: string[];
  };
  sessions?: SessionEntry[];
  appointments?: Appointment[];
  actions?: Action[];
}
const AGENTS: Agent[] = [{
  id: 'finance',
  name: 'Finance',
  icon: DollarSign,
  colour: '#4a9a9a',
  status: 'active',
  purpose: 'Manages spend tracking, invoices, and budget planning',
  voice: 'Sage',
  lastSpoke: 'Monday',
  skills: ['QuickBooks', 'Transactions list', 'Monthly spend summary'],
  soul: {
    goals: ['Keep monthly spend under $5k', 'Automate invoice categorisation', 'Prepare Q2 board report by May'],
    context: ['Switched from Xero to QuickBooks in January', 'Hetzner is the biggest infrastructure cost', 'Acme Corp has a $2,400 outstanding invoice'],
    preferences: ['Prefers weekly spend summary over daily', 'Wants anomalies flagged, not every transaction', 'Currency in GBP']
  },
  sessions: [{
    title: 'Weekly spend review',
    time: 'Monday',
    summary: 'Total spend $299 this week. OpenAI usage up 18% from last week. Flagged Hetzner invoice as higher than usual.'
  }, {
    title: 'Q2 budget prep',
    time: 'last Thursday',
    summary: 'Reviewed draft Q2 budget. Agreed to increase AI/API line to $2,000/month. Infrastructure stays flat.'
  }, {
    title: 'Invoice review',
    time: 'last Monday',
    summary: 'Three new invoices this week. All categorised correctly. Acme Corp invoice still outstanding.'
  }],
  appointments: [{
    title: 'Weekly spend review',
    when: 'Every Monday 9:00 AM',
    recurring: true
  }, {
    title: 'Monthly close prep',
    when: '25th of each month',
    recurring: true
  }, {
    title: 'Q2 board report deadline',
    when: 'May 2'
  }],
  actions: [{
    text: 'Chase Acme Corp on outstanding $2,400 invoice',
    due: 'Friday',
    done: false
  }, {
    text: 'Finalise Q2 budget with Sarah',
    due: 'this week',
    done: false
  }, {
    text: 'Recategorise Hetzner ARM server as Infrastructure',
    done: true
  }, {
    text: 'Set up auto-categorisation rule for OpenAI invoices',
    done: true
  }]
}, {
  id: 'legal',
  name: 'Legal',
  icon: Scale,
  colour: '#9a7a4a',
  status: 'active',
  purpose: 'Reviews contracts, tracks compliance deadlines, manages DPAs',
  voice: 'Ballad',
  lastSpoke: 'yesterday',
  skills: ['Confluence', 'Gmail', 'Google Drive'],
  soul: {
    goals: ['Review all contracts before sign-off', 'Ensure GDPR compliance across all DPAs', 'Track renewal dates'],
    context: ['Acme Corp contract has a liability cap issue in section 4.2', 'DPA with Hetzner was signed in March', 'Using standard TMA contract template'],
    preferences: ['Flag risks, do not summarise obvious terms', 'Always check data retention clauses']
  },
  sessions: [{
    title: 'Acme contract review',
    time: 'yesterday',
    summary: 'Reviewed section 4.2 liability cap. Recommended adjusting from $50k to $100k given project scope.'
  }, {
    title: 'Quarterly compliance check',
    time: 'last week',
    summary: 'All DPAs current. Hetzner renewal coming up in June. No GDPR issues flagged.'
  }],
  appointments: [{
    title: 'Quarterly compliance review',
    when: 'First Monday of each quarter',
    recurring: true
  }, {
    title: 'Acme contract sign-off call',
    when: 'Friday 10:00 AM'
  }],
  actions: [{
    text: 'Send revised section 4.2 to James, Acme Legal',
    due: 'Thursday',
    done: false
  }, {
    text: 'Review Hetzner DPA renewal terms (June)',
    due: 'May 15',
    done: false
  }, {
    text: 'Update contract template with new liability clause',
    done: true
  }]
}, {
  id: 'zenbot-project',
  name: 'Zen Bot Project',
  icon: FolderKanban,
  colour: '#4a9a6a',
  status: 'active',
  purpose: 'Manages the Zen Bot development project',
  voice: 'Marin',
  lastSpoke: 'this morning',
  skills: ['Jira', 'Confluence', 'GitHub CLI'],
  soul: {
    goals: ['Ship UI redesign by end of Q2', 'Resolve rate limiting before next sprint', 'Migrate to any-platform architecture'],
    context: ['Currently on feat/turso-convex-sync-rewrite branch', 'Tom owns rate limiting, Lisa owns SSL cert renewal', 'Using LiveKit for voice, Convex for sync'],
    preferences: ['Focus standup prep on blockers, not status updates', 'Architecture decisions go to Confluence RFC']
  },
  sessions: [{
    title: 'Standup prep',
    time: 'this morning',
    summary: 'Two blockers: rate limiting PR needs review, SSL cert expires Friday. Lisa has a PR ready.'
  }, {
    title: 'Sprint planning',
    time: 'Monday',
    summary: 'Sprint 23 starts next week. Prioritised: UI redesign (13 points), rate limiting (5 points), SSL automation (3 points).'
  }],
  appointments: [{
    title: 'Standup prep',
    when: 'Every weekday 8:50 AM',
    recurring: true
  }, {
    title: 'Sprint review',
    when: 'Every other Friday 3:00 PM',
    recurring: true
  }],
  actions: [{
    text: 'Review Tom rate limiting PR',
    due: 'today',
    done: false
  }, {
    text: 'Approve Lisa SSL cert renewal PR',
    due: 'today',
    done: false
  }, {
    text: 'Write sprint 23 goals in Confluence',
    due: 'Friday',
    done: false
  }]
}, {
  id: 'pa',
  name: 'Personal Assistant',
  icon: User,
  colour: '#7a8a9a',
  status: 'active',
  purpose: 'Manages calendar, travel, errands, and personal admin',
  voice: 'Coral',
  lastSpoke: 'this morning',
  skills: ['Google Calendar', 'Gmail', 'Google Drive'],
  soul: {
    goals: ['Keep calendar conflict-free', 'Handle personal admin before it becomes urgent', 'Book travel two weeks in advance'],
    context: ['Dentist appointment Thursday 4:30pm', 'Car insurance renewal due end of April', 'Prefers morning meetings, afternoons for deep work'],
    preferences: ['Consolidate meetings into blocks', 'No meetings before 9am', 'Travel by train when under 3 hours']
  },
  sessions: [{
    title: 'Morning briefing',
    time: 'this morning',
    summary: 'Three meetings today. Dentist Thursday reminder set. Car insurance renewal flagged for next week.'
  }, {
    title: 'Travel planning',
    time: 'last Wednesday',
    summary: 'Booked train to London for May 8 conference. Return same day. Hotel not needed.'
  }],
  appointments: [{
    title: 'Morning briefing',
    when: 'Every weekday 7:30 AM',
    recurring: true
  }, {
    title: 'Weekly personal admin review',
    when: 'Every Sunday 7:00 PM',
    recurring: true
  }],
  actions: [{
    text: 'Renew car insurance',
    due: 'end of April',
    done: false
  }, {
    text: 'Confirm dentist appointment Thursday',
    due: 'Wednesday',
    done: false
  }, {
    text: 'Book restaurant for Saturday',
    done: true
  }]
}, {
  id: 'trainer',
  name: 'Personal Trainer',
  icon: Dumbbell,
  colour: '#e07a5f',
  status: 'active',
  purpose: 'Plans workouts, tracks progress, adjusts training based on energy and recovery',
  voice: 'Ash',
  lastSpoke: 'yesterday',
  skills: [],
  soul: {
    goals: ['Train 4x per week consistently', 'Improve 5k time to under 22 minutes', 'Maintain upper body strength during running block'],
    context: ['Currently in a running-focused training block', 'Left knee has been tight after long runs', 'Gym access: home gym with rack, dumbbells, pull-up bar'],
    preferences: ['Morning sessions preferred', 'Keep sessions under 45 minutes', 'No heavy squats when knee is tight']
  },
  sessions: [{
    title: 'Session review',
    time: 'yesterday',
    summary: 'Completed 5k in 23:12, down from 23:45 last week. Knee felt fine. Suggested adding hill sprints next week.'
  }, {
    title: 'Weekly plan',
    time: 'Monday',
    summary: 'Mon: easy 3k + upper body. Wed: tempo run 4k. Fri: long run 7k. Sun: recovery walk + stretching.'
  }],
  appointments: [{
    title: 'Weekly training plan',
    when: 'Every Monday 6:30 AM',
    recurring: true
  }, {
    title: 'Session review',
    when: 'After each workout',
    recurring: true
  }],
  actions: [{
    text: 'Add hill sprints to Wednesday tempo session',
    due: 'next week',
    done: false
  }, {
    text: 'Try foam rolling routine for knee',
    due: 'this week',
    done: false
  }, {
    text: 'Update 5k PB tracker',
    done: true
  }]
}, {
  id: 'therapist',
  name: 'Therapist',
  icon: Brain,
  colour: '#8a6a9a',
  status: 'inactive',
  purpose: 'A reflective space for processing thoughts, stress, and emotional patterns',
  voice: 'Sage'
}, {
  id: 'life-coach',
  name: 'Life Coach',
  icon: Compass,
  colour: '#5a8a6a',
  status: 'inactive',
  purpose: 'Helps clarify long-term goals, values, and decisions across life and work',
  voice: 'Ballad'
}, {
  id: 'creative',
  name: 'Creative Assistant',
  icon: Palette,
  colour: '#c4786a',
  status: 'inactive',
  purpose: 'Brainstorming partner for writing, design, naming, and creative projects',
  voice: 'Verse'
}, {
  id: 'health',
  name: 'Health',
  icon: Heart,
  colour: '#9a4a6a',
  status: 'inactive',
  purpose: 'Tracks appointments, medication, and wellness',
  voice: 'Coral'
}, {
  id: 'tma',
  name: 'TMA',
  icon: Briefcase,
  colour: '#6a6a9a',
  status: 'inactive',
  purpose: 'Manages TMA product development and operations',
  voice: 'Ash'
}];
export function AgentsView() {
  const [viewingId, setViewingId] = useState<string | null>(null);
  if (viewingId) {
    const agent = AGENTS.find(a => a.id === viewingId);
    if (agent) return <AgentDetail agent={agent} onBack={() => setViewingId(null)} data-id="element-25" />;
  }
  const active = AGENTS.filter(a => a.status === 'active');
  const inactive = AGENTS.filter(a => a.status === 'inactive');
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-26">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-27">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-8" data-id="element-28">Agents</h1>
        {active.length > 0 && <section className="mb-10" data-id="element-29"><h2 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-30">Active</h2><div className="flex flex-col gap-3" data-id="element-31">{active.map((agent, i) => {
            const Icon = agent.icon;
            return <motion.button key={agent.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.3,
              delay: i * 0.04
            }} onClick={() => setViewingId(agent.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-32"><div className="flex items-center gap-4" data-id="element-33"><div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${agent.colour}15`
                }} data-id="element-34"><Icon size={18} style={{
                    color: agent.colour
                  }} strokeWidth={1.5} data-id="element-35" /></div><div className="flex-1" data-id="element-36"><span className="text-[15px] text-[#e8e4df]" data-id="element-37">{agent.name}</span>{agent.lastSpoke && <p className="text-[12px] text-[#8a8680]/40 mt-0.5" data-id="element-38">{agent.lastSpoke}</p>}</div><div className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                  backgroundColor: agent.colour,
                  opacity: 0.7
                }} data-id="element-39" /></div></motion.button>;
          })}</div></section>}
        {inactive.length > 0 && <section className="mb-10" data-id="element-40"><h2 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-41">Inactive</h2><div className="flex flex-col gap-3" data-id="element-42">{inactive.map((agent, i) => {
            const Icon = agent.icon;
            return <motion.button key={agent.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.3,
              delay: i * 0.04
            }} onClick={() => setViewingId(agent.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-43"><div className="flex items-center gap-4" data-id="element-44"><div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${agent.colour}15`
                }} data-id="element-45"><Icon size={18} style={{
                    color: `${agent.colour}80`
                  }} strokeWidth={1.5} data-id="element-46" /></div><span className="flex-1 text-[15px] text-[#e8e4df]/50" data-id="element-47">{agent.name}</span><span className="text-[12px] text-[#8a8680]/40 group-hover:text-[#8a8680] transition-colors" data-id="element-48">Activate</span></div></motion.button>;
          })}</div></section>}
        <button className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-49"><Plus size={13} data-id="element-50" />New agent</button>
      </div>
    </div>;
}
function AgentDetail({
  agent,
  onBack
}: {
  agent: Agent;
  onBack: () => void;
}) {
  const Icon = agent.icon;
  const [activeTab, setActiveTab] = useState<'soul' | 'sessions' | 'calendar' | 'actions'>('soul');
  if (agent.status === 'inactive') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-51">
        <div className="max-w-[640px] mx-auto w-full" data-id="element-52">
          <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-53"><ArrowLeft size={14} data-id="element-54" /><span className="text-[13px]" data-id="element-55">Agents</span></button>
          <div className="flex flex-col items-center gap-6 py-16" data-id="element-56">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{
            backgroundColor: `${agent.colour}15`
          }} data-id="element-57"><Icon size={28} style={{
              color: agent.colour
            }} strokeWidth={1.5} data-id="element-58" /></div>
            <h1 className="text-[20px] font-medium text-[#e8e4df]" data-id="element-59">{agent.name}</h1>
            {agent.purpose && <p className="text-[14px] text-[#8a8680] text-center max-w-[400px] leading-[1.6]" data-id="element-60">{agent.purpose}</p>}
            <button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors mt-4" data-id="element-61">Activate</button>
          </div>
        </div>
      </motion.div>;
  }
  const labels: Record<string, string> = {
    soul: 'Profile',
    sessions: 'Sessions',
    calendar: 'Calendar',
    actions: 'Actions'
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-62">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-63">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-64"><ArrowLeft size={14} data-id="element-65" /><span className="text-[13px]" data-id="element-66">Agents</span></button>
        <header className="mb-8 flex items-center gap-4" data-id="element-67">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
          backgroundColor: `${agent.colour}15`
        }} data-id="element-68"><Icon size={22} style={{
            color: agent.colour
          }} strokeWidth={1.5} data-id="element-69" /></div>
          <div data-id="element-70"><h1 className="text-[20px] font-medium text-[#e8e4df]" data-id="element-71">{agent.name}</h1><div className="flex items-center gap-3 mt-0.5" data-id="element-72">{agent.voice && <span className="text-[12px] text-[#8a8680]/50" data-id="element-73">{agent.voice}</span>}{agent.lastSpoke && <span className="text-[12px] text-[#8a8680]/40" data-id="element-74">{agent.lastSpoke}</span>}</div></div>
        </header>
        <nav className="flex items-center gap-6 mb-8 border-b border-[#8a8680]/10" data-id="element-75">
          {(['soul', 'sessions', 'calendar', 'actions'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[14px] transition-colors relative ${activeTab === tab ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-76">{labels[tab]}{activeTab === tab && <motion.div layoutId={`agentTab-${agent.id}`} className="absolute bottom-0 left-0 right-0 h-[2px]" style={{
            backgroundColor: agent.colour
          }} initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-77" />}</button>)}
        </nav>

        {activeTab === 'soul' && agent.soul && <div className="flex flex-col gap-8" data-id="element-78">
            <section data-id="element-79"><h3 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-80">Goals</h3><div className="flex flex-col gap-3" data-id="element-81">{agent.soul.goals.map((g, i) => <div key={i} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-82"><p className="text-[14px] text-[#e8e4df]/80 leading-[1.5]" data-id="element-83">{g}</p></div>)}</div></section>
            <section data-id="element-84"><h3 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-85">Context</h3><div className="flex flex-col gap-3" data-id="element-86">{agent.soul.context.map((c, i) => <div key={i} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-87"><p className="text-[14px] text-[#e8e4df]/80 leading-[1.5]" data-id="element-88">{c}</p></div>)}</div></section>
            <section data-id="element-89"><h3 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-90">Preferences</h3><div className="flex flex-col gap-3" data-id="element-91">{agent.soul.preferences.map((p, i) => <div key={i} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-92"><p className="text-[14px] text-[#e8e4df]/80 leading-[1.5]" data-id="element-93">{p}</p></div>)}</div></section>
            {agent.skills && agent.skills.length > 0 && <section data-id="element-94"><h3 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-95">Connected skills</h3><div className="flex items-center gap-3 flex-wrap" data-id="element-96">{agent.skills.map(s => <span key={s} className="text-[13px] text-[#8a8680]" data-id="element-97">{s}</span>)}</div></section>}
          </div>}

        {activeTab === 'sessions' && agent.sessions && <div className="flex flex-col gap-3" data-id="element-98">{agent.sessions.map((s, i) => <motion.div key={i} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-99"><p className="font-semibold text-[#e8e4df] text-[15px]" data-id="element-100">{s.title}</p><p className="text-[14px] text-[#e8e4df]/75 leading-[1.55] mt-2" data-id="element-101">{s.summary}</p><p className="text-[12px] text-[#8a8680]/50 mt-3" data-id="element-102">{s.time}</p></motion.div>)}</div>}

        {activeTab === 'calendar' && agent.appointments && <div className="flex flex-col gap-3" data-id="element-103">{agent.appointments.map((a, i) => <motion.div key={i} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-104"><p className="text-[#e8e4df] text-[15px]" data-id="element-105">{a.title}</p><p className="text-[12px] text-[#8a8680]/50 mt-2" data-id="element-106">{a.when}{a.recurring ? ' (recurring)' : ''}</p></motion.div>)}</div>}

        {activeTab === 'actions' && agent.actions && <div className="flex flex-col gap-3" data-id="element-107">{agent.actions.map((a, i) => <motion.div key={i} initial={{
          opacity: 0
        }} animate={{
          opacity: a.done ? 0.4 : 1
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-108"><p className={`text-[14px] leading-[1.5] ${a.done ? 'text-[#8a8680]/50 line-through' : 'text-[#e8e4df]/80'}`} data-id="element-109">{a.text}</p>{a.due && <p className="text-[12px] text-[#8a8680]/50 mt-2" data-id="element-110">{a.due}</p>}</motion.div>)}</div>}

        <div className="mt-10" data-id="element-111"><button className="text-[14px] hover:text-[#e8e4df] transition-colors" style={{
          color: agent.colour
        }} data-id="element-112">Talk to {agent.name}</button></div>
      </div>
    </motion.div>;
}